import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  query, where, Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { withTimeout, queryCacheFirst } from './utils';
import { ComplianceRecord } from '../models/types';


const COMPLIANCE = 'complianceRecords';

function calcStatus(nextDueDate: string): ComplianceRecord['status'] {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const soon = new Date(today);
  soon.setDate(soon.getDate() + 30);
  const soonStr = `${soon.getFullYear()}-${String(soon.getMonth()+1).padStart(2,'0')}-${String(soon.getDate()).padStart(2,'0')}`;
  if (!nextDueDate) return 'current';  // fix: empty date was classified as overdue ('' < any date string)
  if (nextDueDate < todayStr) return 'overdue';
  if (nextDueDate <= soonStr) return 'due-soon';
  return 'current';
}

function mapRecord(id: string, data: Record<string, any>): ComplianceRecord {
  const nextDueDate = data.nextDueDate?.toDate?.()?.toISOString?.()?.slice(0,10) ?? data.nextDueDate ?? '';
  return {
    id,
    projectId: data.projectId ?? '',
    scheduleId: data.scheduleId ?? undefined,
    assetDescription: data.assetDescription ?? '',
    assetLocation: data.assetLocation ?? undefined,
    standard: data.standard ?? '',
    taskName: data.taskName ?? '',
    frequencyValue: data.frequencyValue ?? 1,
    frequencyUnit: data.frequencyUnit ?? 'years',
    lastCompletedDate: data.lastCompletedDate ?? undefined,
    nextDueDate,
    certificateFiled: data.certificateFiled ?? false,
    certificateNumber: data.certificateNumber ?? undefined,
    inspector: data.inspector ?? undefined,
    notes: data.notes ?? undefined,
    status: calcStatus(nextDueDate),
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? data.updatedAt ?? '',
  };
}

export const createComplianceRecord = async (
  record: Omit<ComplianceRecord, 'id' | 'status' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const now = Timestamp.now();
  // Strip undefined fields — Firestore rejects them
  const clean: Record<string, any> = {};
  Object.entries(record).forEach(([k, v]) => { if (v !== undefined) clean[k] = v; });
  const ref = await withTimeout(addDoc(collection(db, COMPLIANCE), {
    ...clean,
    status: calcStatus(record.nextDueDate),
    createdAt: now,
    updatedAt: now,
  }));
  return ref.id;
};

/** Cache-first; `onRefresh` (optional) gets the fresh server copy in the background. */
export const getComplianceRecords = (projectId: string, onRefresh?: (r: ComplianceRecord[]) => void): Promise<ComplianceRecord[]> =>
  queryCacheFirst(query(collection(db, COMPLIANCE), where('projectId', '==', projectId)), snap =>
    snap.docs.map(d => mapRecord(d.id, d.data())).sort((a, b) => a.nextDueDate.localeCompare(b.nextDueDate)), onRefresh);

export const updateComplianceRecord = async (
  id: string,
  partial: Partial<Omit<ComplianceRecord, 'id' | 'projectId' | 'createdAt' | 'status'>>
): Promise<void> => {
  const updates: Record<string, any> = { updatedAt: Timestamp.now() };
  Object.entries(partial).forEach(([k, v]) => { if (v !== undefined) updates[k] = v; });
  if (partial.nextDueDate) updates.status = calcStatus(partial.nextDueDate);
  await withTimeout(updateDoc(doc(db, COMPLIANCE, id), updates));
};

export const deleteComplianceRecord = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, COMPLIANCE, id)));
};
